import React from 'react';
import { 
    Box, 
    IconButton, 
    Tooltip,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { AiChatPage } from '../../contractsPage/AiChatPage';

const useStyles = makeStyles((theme: any) => ({
    // Base container styles
    chatContainer: {
        backgroundColor: '#ffffff',
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        borderLeft: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        position: 'relative',
        height: '100%', // Ensure it takes full height from parent
        minHeight: 0, // Allow flex shrinking
    },
    chatBody: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    // Action button styles - simple outlined style matching design
    actionButton: {
        width: '32px',
        height: '32px',
        minWidth: '32px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        color: '#666',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#999',
        },
    },
}));

interface AIChatSectionProps {
    open: boolean;
    onClose?: () => void;
    claimAnalysisId?: string | number;
    claimNumber?: string;
    selectedClaimIds?: number[];
    totalClaimsCount?: number;
    externalMessage?: string;
    onMessageSent?: () => void;
    onFiltersUpdate?: (filtersNL: string, filters: any, top_k?: number, sort?: any[]) => void;
    isExpanded?: boolean;
    onExpandToggle?: () => void;
    onClearAllFilters?: () => void;
    preAiModeFilters?: any;
}

const AIChatSection: React.FC<AIChatSectionProps> = ({ 
    open, 
    onClose,
    claimAnalysisId,
    claimNumber,
    selectedClaimIds = [],
    totalClaimsCount = 0,
    externalMessage = '',
    onMessageSent,
    onFiltersUpdate,
    isExpanded = false,
    onExpandToggle,
    onClearAllFilters,
    preAiModeFilters,
}) => {
    const classes = useStyles();

    // Toggle sidebar minimize state when chat opens/closes
    React.useEffect(() => {
        if (open) {
            // Minimize the left sidebar when chat opens
            if (!document.body.classList.contains('kt-aside--minimize')) {
                document.body.classList.add('kt-aside--minimize');
            }
        }
        // Note: We don't restore on close to avoid toggling issues
        // The user can manually expand the sidebar if needed
    }, [open]);

    if (!open) return null;

    // Action buttons to pass to AiChatPage header
    const headerActions = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {isExpanded ? (
                <Tooltip title="Minimize">
                    <IconButton 
                        size="small" 
                        className={classes.actionButton}
                        onClick={onExpandToggle}
                    >
                        <CloseFullscreenIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Expand">
                    <IconButton 
                        size="small" 
                        className={classes.actionButton}
                        onClick={onExpandToggle}
                    >
                        <CropSquareIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            )}
            {onClose && (
                <Tooltip title="Close">
                    <IconButton 
                        size="small" 
                        className={classes.actionButton}
                        onClick={onClose}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );

    return (
        <Box 
            className={`${classes.chatContainer} chat-container-ai-section`}
            sx={{
                width: isExpanded ? '100%' : '400px',
                minWidth: isExpanded ? '100%' : '400px',
                maxWidth: isExpanded ? '100%' : '400px',
                marginLeft: isExpanded ? 0 : '16px',
                marginBottom: '20px', // Match Portlet's kt-portlet class margin-bottom
                height: 'auto !important', // Override the 100% height from chatContainer class
            }}
        >
            {/* Chat Body - AiChatPage with original header */}
            <Box className={classes.chatBody}>
                <AiChatPage 
                    selected={[]}
                    height="100%"
                    defaultTitle="Claims Search"
                    isEmbedded={true}
                    chatType="claim_analysis"
                    claimAnalysisId={claimAnalysisId}
                    claimNumber={claimNumber}
                    selectedClaimIds={selectedClaimIds}
                    totalClaimsCount={totalClaimsCount}
                    headerActions={headerActions}
                    externalMessage={externalMessage}
                    onMessageSent={onMessageSent}
                    onFiltersUpdate={onFiltersUpdate}
                    isSidebarMode={!isExpanded}
                    newChatbutton={{
                        enable: true,
                        text: 'Clear all filters'
                    }}
                    enableResponseFilters={false}
                    enableIconTitle={true}
                    onClearAllFilters={onClearAllFilters}
                    preAiModeFilters={preAiModeFilters}
                    enableFeedback={false}
                />
            </Box>
        </Box>
    );
};

export default AIChatSection;
